// client/src/app/signup/page.js
'use client';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import Image from 'next/image';
import Link from 'next/link';

import { API_URL } from '@/variables';
import { useTenant } from '@/context/TenantContext';
import { updateSchool } from '@/store/slices/schoolSlice';
import { clearLocalSession } from '@/lib/session';
import { GOOGLE_SIGN_IN_URL } from '@/components/landing/landingContent';
import logo from '../../../public/images/logo.png';
import '../../styles/utils.scss';
import '../login/role-login.scss';

const MIN_PASSWORD = 8;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const HIGHLIGHTS = [
  'Students, staff, classes and sections in one place',
  'Attendance, exams, fees and receipts',
  'Public admission form you can customise and print',
  'Works on desktop and phone, no installation',
];

export default function SignupPage() {
  const dispatch = useDispatch();
  const { tenant } = useTenant();
  const [form, setForm] = useState({ schoolName: '', principalName: '', email: '', contactNo: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [emailTaken, setEmailTaken] = useState(false);
  const nameRef = useRef(null);

  useEffect(() => {
    document.title = 'Create your school account';
    if (window.matchMedia?.('(pointer: fine)').matches) nameRef.current?.focus();
  }, []);

  const set = (key) => (e) => {
    setForm((current) => ({ ...current, [key]: e.target.value }));
    if (error) setError(null);
    if (key === 'email') setEmailTaken(false);
  };

  const validate = () => {
    if (form.schoolName.trim().length < 2) return 'Please enter your school name.';
    if (!EMAIL_RE.test(form.email.trim())) return 'Please enter a valid email address.';
    if (form.contactNo.replace(/\D/g, '').length < 7) return 'Please enter a valid contact number.';
    if (form.password.length < MIN_PASSWORD) return `Choose a password of at least ${MIN_PASSWORD} characters.`;
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API_URL}/public/api/auth/school/register`, {
        schoolName: form.schoolName.trim(),
        principalName: form.principalName.trim(),
        email: form.email.trim(),
        contactNo: form.contactNo.trim(),
        password: form.password,
      }, { withCredentials: true });

      clearLocalSession(dispatch);
      dispatch(updateSchool(data.school));
      localStorage.setItem('userRole', 'admin');
      window.location.href = data.redirectUrl || '/dashboard';
    } catch (err) {
      setEmailTaken(err.response?.data?.code === 'EMAIL_EXISTS');
      setError(err.response?.data?.message || 'Could not create your account. Please try again.');
      setSubmitting(false);
    }
  };

  if (!tenant.isPlatformDefault) {
    return (
      <div className="role-login role-admin">
        <div className="role-login-shell" style={{ gridTemplateColumns: '1fr', maxWidth: 480 }}>
          <div className="role-left">
            <h2>Sign in to {tenant.brandName}</h2>
            <p className="lead">New accounts for this portal are created by your school or partner.</p>
            <Link href="/login" className="role-submit" style={{ textAlign: 'center', textDecoration: 'none' }}>Go to sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="role-login role-admin">
      <div className="role-login-shell">
        <div className="role-left">
          <div className="brand-row">
            <Image width={54} height={54} src={logo} alt="Logo" priority />
            <span className="role-pill">New school</span>
          </div>

          <h2>Create your school account</h2>
          <p className="lead">Free to start. Sign in later with your email and this password.</p>

          <form onSubmit={handleSubmit} noValidate>
            <div className="input-group">
              <label htmlFor="schoolName">School name</label>
              <input id="schoolName" ref={nameRef} type="text" autoComplete="organization" placeholder="e.g. St. Xavier High School"
                value={form.schoolName} onChange={set('schoolName')} maxLength={100} />
            </div>

            <div className="input-group">
              <label htmlFor="principalName">Your name <span style={{ fontWeight: 400, color: '#64748b' }}>(optional)</span></label>
              <input id="principalName" type="text" autoComplete="name" placeholder="Principal or owner"
                value={form.principalName} onChange={set('principalName')} maxLength={80} />
            </div>

            <div className="input-group">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" autoComplete="email" inputMode="email" placeholder="you@school.com"
                value={form.email} onChange={set('email')} autoCapitalize="none" spellCheck={false}
                aria-invalid={emailTaken} aria-describedby={error ? 'signup-error' : undefined} />
            </div>

            <div className="input-group">
              <label htmlFor="contactNo">Contact number</label>
              <input id="contactNo" type="tel" autoComplete="tel" inputMode="tel" placeholder="e.g. 9876543210"
                value={form.contactNo} onChange={set('contactNo')} maxLength={20} />
            </div>

            <div className="input-group">
              <label htmlFor="password">Password</label>
              <div className="password-wrapper">
                <input id="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password"
                  placeholder={`At least ${MIN_PASSWORD} characters`} value={form.password} onChange={set('password')} maxLength={72} />
                <button type="button" className="password-toggle-btn" onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}>
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} fixedWidth />
                </button>
              </div>
            </div>

            <button aria-busy={submitting} type="submit" className="role-submit" disabled={submitting}>
              {submitting ? 'Creating your school...' : 'Create account'}
            </button>

            {error && (
              <p id="signup-error" className="error" role="alert">
                {error}{emailTaken && <>{' '}<Link href="/login?role=admin">Sign in</Link></>}
              </p>
            )}
          </form>

          <p className="login-help">
            Already have an account? <Link href="/login?role=admin">Sign in</Link>
            {' · '}
            <Link href={GOOGLE_SIGN_IN_URL}>Continue with Google instead</Link>
          </p>
        </div>

        <div className="role-right">
          <h3>Set up your school in minutes</h3>
          <p>Everything an office needs, ready as soon as you sign up.</p>
          <ul>
            {HIGHLIGHTS.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </div>
      </div>
    </div>
  );
}
